/**
 * NeoCoreRetirosPage — bandeja del operador para validar la cuenta del
 * asociado y acreditar (o rechazar) un retiro solicitado desde la app.
 *
 * Flujo del operador:
 *   1. Ve retiros 'solicitado' (los más viejos primero) → abre el drawer.
 *   2. Coteja la imagen del comprobante de titularidad contra los datos:
 *      el titular de la cuenta bancaria debe ser el mismo asociado.
 *   3. "Acreditar" → RPC acreditar_retiro_admin (DEBITA el ahorro).
 *      "Rechazar" → RPC rechazar_retiro_admin (pide motivo).
 *
 * La imagen vive en el bucket privado 'retiros'; se muestra con signed URL
 * (TTL 5 min) generada al abrir el drawer.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowsClockwise, DownloadSimple, HandCoins, Lightning, MagnifyingGlass } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { bajarCsvAch, lineasAch } from '@/lib/achInterbanco'
import { RetirosExpressBandeja } from '@/components/RetirosExpressBandeja'

type Estado = 'solicitado' | 'acreditado' | 'rechazado'

type RetiroRow = {
  id: string
  asociado_id: string
  cuenta_bancaria_id: string | null
  cuenta_ahorro_id: string | null
  monto: number
  estado: Estado
  comprobante_titularidad_path: string | null
  movimiento_cuenta_id: string | null
  motivo_rechazo: string | null
  forma_pago: string | null
  cuenta_salida_codigo: string | null
  referencia_banco: string | null
  solicitado_at: string
  procesado_at: string | null
  procesado_por_user_id: string | null
  // Retiro Express (ADR 025)
  tipo: 'normal' | 'express'
  comision: number
  sla_vence_at: string | null
  comision_cobrada: number | null
  monto_transferido: number | null
  comision_devuelta: boolean
  lote_id: string | null
  monto_lote: number | null
  // join asociado
  asociado_nombre: string
  asociado_no: string
  asociado_neo: string
  // join cuenta bancaria destino
  banco: string
  tipo_cuenta: string
  numero_cuenta: string
  titular_nombre: string
  cuenta_estado: string | null
  cuenta_comprobante_path: string | null
}

const ESTADO_BADGE: Record<Estado, { label: string; color: string; bg: string }> = {
  solicitado: { label: 'Solicitado', color: 'var(--warning, #d97706)', bg: 'rgba(217,119,6,0.10)' },
  acreditado: { label: 'Acreditado', color: 'var(--ok-ink, #0f8a57)', bg: 'rgba(15,138,87,0.10)' },
  rechazado:  { label: 'Rechazado', color: 'var(--danger, #a32d2d)', bg: 'rgba(163,45,45,0.10)' },
}

const fmtQ = (n: number) =>
  'Q ' + Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtFechaHora = (iso: string | null) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-GT', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'America/Guatemala',
  })
}

// Fecha "hoy" en hora de Guatemala (YYYY-MM-DD) para filtrar los del día.
const hoyGT = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guatemala' })
const esHoyGT = (iso: string | null) =>
  !!iso && new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/Guatemala' }) === hoyGT()

type Filtro = Estado | 'hoy' | 'express'

// Express: se cobra la comisión si se acredita hasta el vencimiento + 10 min de margen
// (misma regla que retiro_monto_a_transferir en la base; el servidor valida al acreditar).
const MARGEN_EXPRESS_MS = 10 * 60_000
const cobraExpress = (r: RetiroRow, now: number) =>
  r.tipo === 'express' && r.comision > 0 && !!r.sla_vence_at &&
  now <= new Date(r.sla_vence_at).getTime() + MARGEN_EXPRESS_MS
const aTransferir = (r: RetiroRow, now: number) => r.monto - (cobraExpress(r, now) ? r.comision : 0)
const minutosRestantes = (r: RetiroRow, now: number) =>
  r.sla_vence_at ? Math.round((new Date(r.sla_vence_at).getTime() - now) / 60_000) : null
const horaGT = (iso: string | null) => iso
  ? new Date(iso).toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Guatemala' })
  : '—'

function ExpressChip({ r, now }: { r: RetiroRow; now: number }) {
  if (r.tipo !== 'express') return null
  if (r.estado !== 'solicitado') {
    return (
      <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--brand-700)' }}>
        ⚡ Express · {r.comision_devuelta ? 'comisión devuelta' : `comisión ${fmtQ(r.comision_cobrada ?? r.comision)}`}
      </span>
    )
  }
  const m = minutosRestantes(r, now)
  const vencido = m !== null && m < 0
  const urgente = m !== null && m <= 10
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 99,
      background: vencido ? 'rgba(163,45,45,0.12)' : urgente ? 'rgba(217,119,6,0.14)' : 'var(--brand-50)',
      color: vencido ? 'var(--danger, #a32d2d)' : urgente ? 'var(--warning, #d97706)' : 'var(--brand-700)',
      whiteSpace: 'nowrap',
    }}>
      <Lightning size={11} weight="fill" />
      {vencido ? `VENCIDO ${horaGT(r.sla_vence_at)}` : `Express · vence ${horaGT(r.sla_vence_at)} (${m} min)`}
    </span>
  )
}

export function NeoCoreRetirosPage() {
  const [rows, setRows] = useState<RetiroRow[]>([])
  const [loading, setLoading] = useState(true)
  // ?vista=express (link del WhatsApp de Elder) abre directo la bandeja Express.
  const [filtro, setFiltro] = useState<Filtro>(() =>
    new URLSearchParams(window.location.search).get('vista') === 'express' ? 'express' : 'solicitado')
  const [search, setSearch] = useState('')
  const [drawer, setDrawer] = useState<RetiroRow | null>(null)
  // Reloj para los cronómetros de Express.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 15_000); return () => clearInterval(t) }, [])
  // Interruptor del Retiro Express.
  const [rx, setRx] = useState<{ activo: boolean; disponible: boolean; motivo: string | null } | null>(null)
  const [rxBusy, setRxBusy] = useState(false)
  const cargarRx = useCallback(async () => {
    const { data } = await supabase.rpc('retiro_express_estado')
    if (data) setRx(data as { activo: boolean; disponible: boolean; motivo: string | null })
  }, [])
  useEffect(() => { void cargarRx() }, [cargarRx])
  const toggleRx = async () => {
    if (!rx) return
    const nuevo = !rx.activo
    if (!confirm(nuevo
      ? '¿Encender el Retiro Express? Los asociados van a poder pedirlo (Q25, transferencia en menos de 30 min).'
      : '¿Apagar el Retiro Express? Solo quedará el retiro Normal.')) return
    setRxBusy(true)
    const { data, error } = await supabase.rpc('retiro_express_set_activo', { p_activo: nuevo })
    setRxBusy(false)
    if (error) { alert('Error: ' + error.message); return }
    setRx(data as { activo: boolean; disponible: boolean; motivo: string | null })
  }
  // IDs de retiros elegidos para exportar (solo aplica a 'solicitado').
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase
      .from('retiro')
      .select(`
        id, asociado_id, cuenta_bancaria_id, cuenta_ahorro_id, monto, estado,
        comprobante_titularidad_path, movimiento_cuenta_id, motivo_rechazo,
        forma_pago, cuenta_salida_codigo, referencia_banco,
        solicitado_at, procesado_at, procesado_por_user_id,
        tipo, comision, sla_vence_at, comision_cobrada, monto_transferido, comision_devuelta, lote_id, monto_lote,
        asociado:asociado_id (no_asociado, neo_id, nombre_completo),
        cuenta_bancaria:cuenta_bancaria_id (banco, tipo_cuenta, numero_cuenta, titular_nombre, estado, comprobante_titularidad_path)
      `)
      // DESC + límite alto: garantiza que TODOS los solicitados (los más nuevos)
      // entren, así los contadores/KPI/export cuadran (antes se cortaban con limit 300 asc).
      .order('solicitado_at', { ascending: false })
      .limit(2000)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mapped: RetiroRow[] = (data ?? []).map((r: any) => ({
      id: r.id,
      asociado_id: r.asociado_id,
      cuenta_bancaria_id: r.cuenta_bancaria_id,
      cuenta_ahorro_id: r.cuenta_ahorro_id,
      monto: Number(r.monto ?? 0),
      estado: r.estado,
      comprobante_titularidad_path: r.comprobante_titularidad_path,
      movimiento_cuenta_id: r.movimiento_cuenta_id,
      motivo_rechazo: r.motivo_rechazo,
      forma_pago: r.forma_pago ?? null,
      cuenta_salida_codigo: r.cuenta_salida_codigo ?? null,
      referencia_banco: r.referencia_banco ?? null,
      solicitado_at: r.solicitado_at,
      procesado_at: r.procesado_at,
      procesado_por_user_id: r.procesado_por_user_id,
      tipo: r.tipo === 'express' ? 'express' : 'normal',
      comision: Number(r.comision ?? 0),
      sla_vence_at: r.sla_vence_at ?? null,
      comision_cobrada: r.comision_cobrada == null ? null : Number(r.comision_cobrada),
      monto_transferido: r.monto_transferido == null ? null : Number(r.monto_transferido),
      comision_devuelta: !!r.comision_devuelta,
      lote_id: r.lote_id ?? null,
      monto_lote: r.monto_lote == null ? null : Number(r.monto_lote),
      asociado_nombre: r.asociado?.nombre_completo ?? '—',
      asociado_no: r.asociado?.no_asociado ?? '—',
      asociado_neo: r.asociado?.neo_id ?? r.asociado?.no_asociado ?? '',
      banco: r.cuenta_bancaria?.banco ?? '—',
      tipo_cuenta: r.cuenta_bancaria?.tipo_cuenta ?? '—',
      numero_cuenta: r.cuenta_bancaria?.numero_cuenta ?? '—',
      titular_nombre: r.cuenta_bancaria?.titular_nombre ?? '—',
      cuenta_estado: r.cuenta_bancaria?.estado ?? null,
      cuenta_comprobante_path: r.cuenta_bancaria?.comprobante_titularidad_path ?? null,
    }))
    setRows(mapped)
    setLoading(false)
  }, [])

  useEffect(() => { void load() }, [load])

  // Auto-refresh cada 60s para que Nataly vea retiros nuevos sin recargar.
  useEffect(() => {
    const t = setInterval(() => { void load() }, 60_000)
    return () => clearInterval(t)
  }, [load])

  const counts = useMemo(() => ({
    hoy: rows.filter((r) => esHoyGT(r.solicitado_at)).length,
    express: rows.filter((r) => r.tipo === 'express' && r.estado === 'solicitado').length,
    solicitado: rows.filter((r) => r.estado === 'solicitado').length,
    acreditado: rows.filter((r) => r.estado === 'acreditado').length,
    rechazado: rows.filter((r) => r.estado === 'rechazado').length,
  }), [rows])

  // Resumen operativo: cuánto hay pendiente de mover (los 'solicitado').
  const pendientes = useMemo(() => {
    const list = rows.filter((r) => r.estado === 'solicitado')
    return {
      n: list.length,
      total: list.reduce((s, r) => s + r.monto, 0),
      acreditadoHoy: rows
        .filter((r) => r.estado === 'acreditado' && esHoyGT(r.procesado_at))
        .reduce((s, r) => s + r.monto, 0),
    }
  }, [rows])

  // Exporta los retiros SOLICITADOS al formato de carga por archivo ACH de
  // InterBanking (8 columnas, sin encabezado, códigos numéricos, sin tildes).
  const exportarLoteACH = () => {
    // Si hay retiros seleccionados, se exportan SOLO esos. Si no, todos los solicitados.
    // Los Express van por su propia bandeja (lotes Express), salvo que se seleccionen a mano.
    const base = rows.filter((r) => r.estado === 'solicitado')
    const pend = selected.size > 0
      ? base.filter((r) => selected.has(r.id))
      : base.filter((r) => r.tipo !== 'express')
    if (pend.length === 0) {
      alert(selected.size > 0
        ? 'No hay retiros seleccionados para exportar.'
        : 'No hay retiros solicitados para exportar.')
      return
    }
    const ahora = Date.now()
    const { lineas, sinCodigo } = lineasAch(pend.map((r) => ({
      asociado_nombre: r.asociado_nombre, asociado_neo: r.asociado_neo, banco: r.banco,
      tipo_cuenta: r.tipo_cuenta, numero_cuenta: r.numero_cuenta,
      monto: r.lote_id && r.monto_lote != null ? r.monto_lote : aTransferir(r, ahora),
    })))
    if (sinCodigo.length > 0) {
      alert(
        'Estos retiros tienen un banco sin código ACH en el diccionario y NO se incluyeron ' +
        '(cargalos manual):\n\n' + sinCodigo.join('\n'),
      )
    }
    if (lineas.length === 0) return
    const archivos = bajarCsvAch(lineas, `retiros_lote_ach_${hoyGT().replace(/-/g, '')}`)
    if (archivos > 1) {
      alert(`El lote tiene ${lineas.length} retiros. Interbanco acepta máx. 9 por archivo, ` +
        `así que se bajan ${archivos} archivos (grupos de 9). Subilos uno por uno al banco.`)
    }
  }

  const filtered = useMemo(() => {
    let list = filtro === 'hoy'
      ? rows.filter((r) => esHoyGT(r.solicitado_at))
      : filtro === 'express'
        ? rows.filter((r) => r.tipo === 'express')
        : rows.filter((r) => r.estado === filtro)
    const q = search.trim().toLowerCase()
    if (q) {
      list = list.filter((r) =>
        r.asociado_nombre.toLowerCase().includes(q) ||
        r.asociado_no.toLowerCase().includes(q) ||
        r.numero_cuenta.includes(q) ||
        r.banco.toLowerCase().includes(q),
      )
    }
    // Solicitados/hoy: los más viejos primero (FIFO de procesamiento).
    // Acreditados/rechazados: los más recientes primero.
    const asc = filtro === 'solicitado' || filtro === 'hoy'
    // Express pendientes siempre arriba (tienen reloj).
    const prio = (r: RetiroRow) => (r.estado === 'solicitado' && r.tipo === 'express' ? 0 : 1)
    return [...list].sort((a, b) =>
      prio(a) - prio(b) ||
      (asc ? (a.solicitado_at < b.solicitado_at ? -1 : 1)
           : (a.solicitado_at < b.solicitado_at ? 1 : -1)))
  }, [rows, filtro, search])

  // Selección para exportar solo los retiros elegidos.
  const seleccion = useMemo(() => {
    const list = rows.filter((r) => r.estado === 'solicitado' && selected.has(r.id))
    return { n: list.length, total: list.reduce((s, r) => s + r.monto, 0) }
  }, [rows, selected])
  const solicitadoVisibles = useMemo(
    () => filtered.filter((r) => r.estado === 'solicitado'),
    [filtered],
  )
  const allVisiblesSelected =
    solicitadoVisibles.length > 0 && solicitadoVisibles.every((r) => selected.has(r.id))
  const toggleOne = (id: string) =>
    setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const toggleAllVisibles = () =>
    setSelected((prev) => {
      const n = new Set(prev)
      if (allVisiblesSelected) solicitadoVisibles.forEach((r) => n.delete(r.id))
      else solicitadoVisibles.forEach((r) => n.add(r.id))
      return n
    })
  const limpiarSeleccion = () => setSelected(new Set())

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1300, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 18, gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em' }}>
            Retiros
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--muted-foreground)' }}>
            Validá la cuenta del asociado y acreditá el retiro.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {rx && (
            <button
              onClick={toggleRx}
              disabled={rxBusy}
              className="btn btn-ghost"
              title={rx.activo ? (rx.disponible ? 'Express encendido y disponible ahora' : (rx.motivo ?? '')) : 'Express apagado'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                borderColor: rx.activo ? 'var(--brand)' : 'var(--hairline)',
                color: rx.activo ? 'var(--brand-700)' : 'var(--muted-foreground)',
              }}
            >
              <Lightning size={14} weight={rx.activo ? 'fill' : 'regular'} />
              Express {rx.activo ? (rx.disponible ? 'encendido' : 'encendido · fuera de horario') : 'apagado'}
            </button>
          )}
          <button
            onClick={exportarLoteACH}
            disabled={counts.solicitado === 0}
            className="btn btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, opacity: counts.solicitado === 0 ? 0.5 : 1 }}
            title={seleccion.n > 0
              ? `Exporta solo los ${seleccion.n} retiros seleccionados al .csv de InterBanking (ACH)`
              : 'Genera el .csv de todos los solicitados para Carga por Archivo en InterBanking (ACH). Seleccioná filas para exportar solo esas.'}
          >
            <DownloadSimple size={14} weight="bold" /> Exportar lote ACH ({seleccion.n > 0 ? seleccion.n : counts.solicitado})
          </button>
          <button onClick={() => void load()} className="btn btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ArrowsClockwise size={13} weight="bold" /> Refrescar
          </button>
        </div>
      </div>

      {/* Resumen operativo · qué hay pendiente de mover */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{
          flex: 1, minWidth: 200,
          background: pendientes.n > 0 ? 'rgba(217,119,6,0.08)' : 'var(--surface)',
          border: '1px solid', borderColor: pendientes.n > 0 ? 'rgba(217,119,6,0.30)' : 'var(--hairline)',
          borderRadius: 12, padding: '14px 16px',
        }}>
          <p style={{ margin: 0, fontSize: 10, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--warning, #d97706)' }}>
            Pendiente de acreditar
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 22, fontWeight: 800, color: 'var(--ink)', fontFeatureSettings: '"tnum"' }}>
            {fmtQ(pendientes.total)}
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted-foreground)', marginLeft: 8 }}>
              {pendientes.n} {pendientes.n === 1 ? 'retiro' : 'retiros'}
            </span>
          </p>
        </div>
        <div style={{
          flex: 1, minWidth: 200,
          background: 'var(--surface)', border: '1px solid var(--hairline)',
          borderRadius: 12, padding: '14px 16px',
        }}>
          <p style={{ margin: 0, fontSize: 10, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--ok-ink, #0f8a57)' }}>
            Acreditado hoy
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 22, fontWeight: 800, color: 'var(--ink)', fontFeatureSettings: '"tnum"' }}>
            {fmtQ(pendientes.acreditadoHoy)}
          </p>
        </div>
      </div>

      {/* Filtros + búsqueda */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        borderRadius: 12, padding: '12px 14px', marginBottom: 12,
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {([
            ['hoy', 'Hoy', counts.hoy],
            ['express', '⚡ Express', counts.express],
            ['solicitado', 'Solicitados', counts.solicitado],
            ['acreditado', 'Acreditados', counts.acreditado],
            ['rechazado', 'Rechazados', counts.rechazado],
          ] as Array<[Filtro, string, number]>).map(([key, label, n]) => {
            const active = filtro === key
            return (
              <button
                key={key}
                onClick={() => setFiltro(key)}
                style={{
                  all: 'unset', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', borderRadius: 99,
                  background: active ? 'var(--brand-50)' : 'transparent',
                  color: active ? 'var(--brand-700)' : 'var(--ink-soft)',
                  fontSize: 12, fontWeight: 700,
                  border: '1px solid', borderColor: active ? 'var(--brand-300, transparent)' : 'transparent',
                }}
              >
                {label}
                <span style={{
                  fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 99,
                  background: active ? 'var(--brand)' : 'var(--n-100)',
                  color: active ? '#fff' : 'var(--muted-foreground)',
                  fontFeatureSettings: '"tnum"',
                }}>{n}</span>
              </button>
            )
          })}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'var(--bg)', borderRadius: 8,
          padding: '6px 10px', border: '1px solid var(--hairline)',
          minWidth: 240,
        }}>
          <MagnifyingGlass size={14} weight="bold" color="var(--muted-foreground)" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Asociado, banco, # cuenta…"
            style={{
              flex: 1, border: 0, background: 'transparent',
              outline: 'none', fontSize: 13, fontFamily: 'inherit',
              color: 'var(--ink)',
            }}
          />
        </div>
      </div>

      {/* Barra de selección · aparece cuando hay retiros elegidos */}
      {seleccion.n > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          background: 'var(--brand-50)', border: '1px solid var(--brand-300, var(--brand-200))',
          borderRadius: 10, padding: '10px 14px', marginBottom: 12,
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand-700)' }}>
            {seleccion.n} {seleccion.n === 1 ? 'retiro seleccionado' : 'retiros seleccionados'} · {fmtQ(seleccion.total)}
          </span>
          <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
            Se exportarán solo estos en el lote ACH.
          </span>
          <div style={{ flex: 1 }} />
          <button onClick={limpiarSeleccion} className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }}>
            Limpiar selección
          </button>
        </div>
      )}

      {filtro === 'express' && (
        <RetirosExpressBandeja
          rows={rows}
          now={now}
          onOpen={(id) => { const r = rows.find((x) => x.id === id); if (r) setDrawer(r) }}
          onReload={() => void load()}
        />
      )}

      {/* Tabla */}
      {filtro !== 'express' && <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        borderRadius: 12, overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>
            <HandCoins size={28} weight="duotone" /><br />
            Sin retiros con este filtro.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--hairline)', background: 'var(--bg)' }}>
                <th style={{ ...th, width: 36 }}>
                  <input
                    type="checkbox"
                    aria-label="Seleccionar todos los solicitados visibles"
                    checked={allVisiblesSelected}
                    disabled={solicitadoVisibles.length === 0}
                    onChange={toggleAllVisibles}
                    style={{ cursor: solicitadoVisibles.length === 0 ? 'default' : 'pointer' }}
                  />
                </th>
                <th style={th}>Fecha</th>
                <th style={th}>Asociado</th>
                <th style={{ ...th, textAlign: 'right' }}>Monto</th>
                <th style={th}>Cuenta destino</th>
                <th style={th}>Titular</th>
                <th style={th}>Estado</th>
                <th style={th}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const badge = ESTADO_BADGE[r.estado]
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--line-soft)' }}>
                    <td style={{ ...td, width: 36 }}>
                      {r.estado === 'solicitado' && (
                        <input
                          type="checkbox"
                          aria-label={`Seleccionar retiro de ${r.asociado_nombre}`}
                          checked={selected.has(r.id)}
                          onChange={() => toggleOne(r.id)}
                          style={{ cursor: 'pointer' }}
                        />
                      )}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtFechaHora(r.solicitado_at)}</td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.asociado_nombre}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{r.asociado_no}</div>
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, fontFeatureSettings: '"tnum"', whiteSpace: 'nowrap' }}>
                      {fmtQ(r.monto)}
                      {r.tipo === 'express' && r.estado === 'solicitado' && (
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand-700)' }}>
                          transferir {fmtQ(aTransferir(r, now))}
                        </div>
                      )}
                      {r.tipo === 'express' && (
                        <div style={{ marginTop: 3 }}><ExpressChip r={r} now={now} /></div>
                      )}
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.banco}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted-foreground)', fontFeatureSettings: '"tnum"' }}>
                        {r.tipo_cuenta} · {r.numero_cuenta}
                      </div>
                    </td>
                    <td style={td}>{r.titular_nombre}</td>
                    <td style={td}>
                      <span style={{
                        fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 99,
                        background: badge.bg, color: badge.color,
                        textTransform: 'uppercase', letterSpacing: 0.5,
                      }}>
                        {badge.label}
                      </span>
                    </td>
                    <td style={td}>
                      <button
                        onClick={() => setDrawer(r)}
                        className="btn btn-ghost"
                        style={{ fontSize: 12, padding: '5px 12px' }}
                      >
                        {r.estado === 'solicitado' ? 'Revisar' : 'Ver'}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>}

      {drawer && (
        <RetiroDrawer
          retiro={drawer}
          onClose={() => setDrawer(null)}
          onChange={() => { void load(); setDrawer(null) }}
        />
      )}
    </div>
  )
}

const th: React.CSSProperties = {
  textAlign: 'left', padding: '10px 14px',
  fontSize: 10, fontWeight: 800, color: 'var(--muted-foreground)',
  textTransform: 'uppercase', letterSpacing: 1.2,
}
const td: React.CSSProperties = { padding: '12px 14px' }

// ============================================================
// Drawer de detalle / acciones
// ============================================================

function RetiroDrawer({
  retiro, onClose, onChange,
}: {
  retiro: RetiroRow
  onClose: () => void
  onChange: () => void
}) {
  const [submitting, setSubmitting] = useState(false)
  const [imgUrl, setImgUrl] = useState<string | null>(null)
  const [nota, setNota] = useState('')
  const [motivoRechazo, setMotivoRechazo] = useState('')
  const [formaPago, setFormaPago] = useState<'EFECTIVO' | 'TRANSFERENCIA'>('TRANSFERENCIA')
  const [cuentasSalida, setCuentasSalida] = useState<Array<{ codigo: string; nombre: string; tipo: string }>>([])
  const [cuentaSalida, setCuentaSalida] = useState<string>('')
  const [refBanco, setRefBanco] = useState('')
  // Express: cuánto transferir AHORA (lo decide el servidor; se refresca cada 20 s).
  const [calc, setCalc] = useState<{ cobra_comision: boolean; comision: number; monto_a_transferir: number; sla_vence_at: string | null } | null>(null)
  useEffect(() => {
    if (retiro.estado !== 'solicitado') return
    let cancel = false
    const cargar = () => supabase.rpc('retiro_monto_a_transferir', { p_retiro_id: retiro.id }).then(({ data }) => {
      if (cancel || !data) return
      const d = data as { cobra_comision: boolean; comision: number; monto_a_transferir: number; sla_vence_at: string | null }
      setCalc({ ...d, comision: Number(d.comision), monto_a_transferir: Number(d.monto_a_transferir) })
    })
    void cargar()
    const t = setInterval(cargar, 20_000)
    return () => { cancel = true; clearInterval(t) }
  }, [retiro.id, retiro.estado])
  const montoTransferir = calc?.monto_a_transferir ?? retiro.monto

  // Pasar a Express un retiro Normal cuando el asociado lo acepta (comisión fija Q25).
  const [consent, setConsent] = useState('')
  const pasarAExpress = async () => {
    if (!consent.trim()) { alert('Anotá cómo aceptó el asociado (ej. "WhatsApp 12:05: SÍ")'); return }
    setSubmitting(true)
    const { data, error } = await supabase.rpc('convertir_retiro_express_admin', { p_retiro_id: retiro.id, p_consentimiento: consent.trim() })
    setSubmitting(false)
    if (error) { alert('Error: ' + error.message); return }
    const r = data as { monto_a_transferir: number; vence: string }
    alert(`Ahora es Express: transferir ${fmtQ(Number(r.monto_a_transferir))} antes de las ${r.vence}. Ya está en la pestaña ⚡ Express.`)
    onChange()
  }

  // Cuentas de salida (caja + bancos) para la partida contable / conciliación.
  useEffect(() => {
    let cancelled = false
    supabase.rpc('listar_cuentas_salida_retiro').then(({ data }) => {
      if (cancelled || !data) return
      const list = data as Array<{ codigo: string; nombre: string; tipo: string }>
      setCuentasSalida(list)
      // Default: Interbanco (los retiros salen de ahí); si no está, primer banco.
      const interbanco = list.find((c) => c.nombre.toLowerCase().includes('interbanco'))
      const pre = interbanco ?? list.find((c) => c.tipo === 'BANCO')
      if (pre) setCuentaSalida(pre.codigo)
    })
    return () => { cancelled = true }
  }, [])

  // Imagen de titularidad de la cuenta. Desde el flujo nuevo la foto vive en
  // el bucket 'cuentas' (se sube al verificar la cuenta, no en el retiro).
  // Los retiros viejos tenían su propia foto en el bucket 'retiros'.
  // Es OPCIONAL: si falta o falla, no mostramos error (la cuenta ya se
  // verificó antes del retiro en Verificaciones bancarias).
  const comprobantePath = retiro.comprobante_titularidad_path ?? retiro.cuenta_comprobante_path
  const comprobanteBucket = retiro.comprobante_titularidad_path ? 'retiros' : 'cuentas'

  useEffect(() => {
    if (!comprobantePath) { setImgUrl(null); return }
    let cancelled = false
    supabase.storage
      .from(comprobanteBucket)
      .createSignedUrl(comprobantePath, 300) // 5 min TTL
      .then(({ data, error }) => {
        if (cancelled) return
        setImgUrl(error || !data ? null : data.signedUrl)
      })
      .catch(() => { if (!cancelled) setImgUrl(null) })
    return () => { cancelled = true }
  }, [comprobantePath, comprobanteBucket])

  const acreditar = async () => {
    if (formaPago === 'TRANSFERENCIA' && !cuentaSalida) {
      alert('Elegí de qué cuenta de banco sale la transferencia'); return
    }
    if (formaPago === 'TRANSFERENCIA' && !refBanco.trim()) {
      alert('Cargá la referencia de la transferencia antes de acreditar'); return
    }
    setSubmitting(true)
    const { data, error } = await supabase.rpc('acreditar_retiro_admin', {
      p_retiro_id: retiro.id,
      p_forma_pago: formaPago,
      p_cuenta_salida_codigo: formaPago === 'TRANSFERENCIA' ? cuentaSalida : null,
      p_nota: nota || null,
      p_referencia_banco: formaPago === 'TRANSFERENCIA' ? (refBanco.trim() || null) : null,
      // Lo que se mandó al banco: el servidor valida que coincida con la regla del Express.
      p_monto_transferido: montoTransferir,
    })
    setSubmitting(false)
    if (error) { alert('Error: ' + error.message); return }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res = data as any
    if (res && res.ok === false) { alert('No se pudo acreditar: ' + (res.error ?? 'error desconocido')); return }
    if (res && typeof res.saldo_nuevo !== 'undefined') {
      alert('Retiro acreditado. Nuevo saldo de ahorro: ' + fmtQ(Number(res.saldo_nuevo)))
    }
    onChange()
  }

  const rechazar = async () => {
    if (!motivoRechazo.trim()) { alert('Ingresá un motivo'); return }
    setSubmitting(true)
    const { error } = await supabase.rpc('rechazar_retiro_admin', {
      p_retiro_id: retiro.id,
      p_motivo: motivoRechazo,
    })
    setSubmitting(false)
    if (error) { alert('Error: ' + error.message); return }
    onChange()
  }

  const badge = ESTADO_BADGE[retiro.estado]
  const esSolicitado = retiro.estado === 'solicitado'

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,18,25,0.40)', zIndex: 999 }} />
      <aside style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 'min(520px, 100%)', background: '#fff', zIndex: 1000,
        display: 'flex', flexDirection: 'column',
        boxShadow: '-12px 0 32px rgba(0,0,0,0.10)',
      }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--hairline)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Retiro</h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>
              {retiro.asociado_nombre} · {retiro.asociado_no}
            </p>
          </div>
          <span style={{
            fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 99,
            background: badge.bg, color: badge.color,
            textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap',
          }}>
            {badge.label}
          </span>
        </div>

        <div style={{ padding: '18px 24px', overflowY: 'auto', flex: 1 }}>
          <div style={{
            background: 'var(--brand-50)', border: '1px solid var(--brand-300, var(--brand-200))',
            borderRadius: 10, padding: '14px 16px', marginBottom: 16,
          }}>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 800, color: 'var(--brand-700)', textTransform: 'uppercase', letterSpacing: 1.5 }}>
              Monto a retirar
            </p>
            <p style={{ margin: '6px 0 0', fontSize: 28, fontWeight: 800, color: 'var(--brand)', fontFeatureSettings: '"tnum"' }}>
              {fmtQ(retiro.monto)}
            </p>
            {retiro.tipo === 'express' && esSolicitado && calc && (
              <div style={{
                marginTop: 10, padding: '10px 12px', borderRadius: 8,
                background: calc.cobra_comision ? '#fff' : 'rgba(163,45,45,0.08)',
                border: '1px solid', borderColor: calc.cobra_comision ? 'var(--brand-300, var(--brand-200))' : 'rgba(163,45,45,0.3)',
                fontSize: 12.5, lineHeight: 1.5,
              }}>
                <strong>⚡ Retiro Express</strong> · vence {horaGT(calc.sla_vence_at)}<br />
                {calc.cobra_comision ? (
                  <>Transferí exactamente <strong style={{ fontSize: 15 }}>{fmtQ(calc.monto_a_transferir)}</strong> (comisión {fmtQ(calc.comision)}).</>
                ) : (
                  <span style={{ color: 'var(--danger, #a32d2d)' }}>
                    Venció: <strong>no se cobra la comisión</strong>. Transferí el monto completo <strong style={{ fontSize: 15 }}>{fmtQ(calc.monto_a_transferir)}</strong>.
                  </span>
                )}
              </div>
            )}
            {retiro.tipo === 'express' && !esSolicitado && retiro.estado === 'acreditado' && (
              <p style={{ margin: '8px 0 0', fontSize: 12 }}>
                ⚡ Express · transferido {fmtQ(retiro.monto_transferido ?? retiro.monto)} ·{' '}
                {retiro.comision_devuelta ? 'comisión devuelta (fuera de tiempo)' : `comisión ${fmtQ(retiro.comision_cobrada ?? 0)}`}
              </p>
            )}
          </div>

          <Info label="Solicitado" value={fmtFechaHora(retiro.solicitado_at)} />
          <Info label="Banco" value={`${retiro.banco} · ${retiro.tipo_cuenta}`} />
          <Info label="Número de cuenta" value={retiro.numero_cuenta} mono />
          <Info label="Titular de la cuenta" value={retiro.titular_nombre} highlight />
          <Info label="Asociado" value={retiro.asociado_nombre} />
          {retiro.cuenta_estado && <Info label="Estado de la cuenta" value={retiro.cuenta_estado} />}
          {retiro.estado !== 'solicitado' && (
            <Info label="Procesado" value={fmtFechaHora(retiro.procesado_at)} />
          )}
          {retiro.estado === 'acreditado' && retiro.forma_pago && (
            <Info label="Forma de pago" value={retiro.forma_pago === 'EFECTIVO' ? 'Efectivo (Caja)' : 'Transferencia'} />
          )}
          {retiro.estado === 'acreditado' && retiro.cuenta_salida_codigo && (
            <Info
              label="Salió de"
              value={cuentasSalida.find((c) => c.codigo === retiro.cuenta_salida_codigo)?.nombre ?? retiro.cuenta_salida_codigo}
            />
          )}
          {retiro.estado === 'acreditado' && retiro.referencia_banco && (
            <Info label="Referencia" value={retiro.referencia_banco} />
          )}
          {retiro.estado === 'acreditado' && (
            <div style={{
              marginTop: 6, marginBottom: 4,
              border: '1px solid rgba(15,138,87,0.22)', background: 'rgba(15,138,87,0.06)',
              borderRadius: 8, padding: '8px 12px', fontSize: 11.5, color: 'var(--ok-ink, #0f8a57)',
            }}>
              ✓ Contabilizado (DEBE ahorro / HABER {retiro.forma_pago === 'EFECTIVO' ? 'caja' : 'banco'}).
              {retiro.forma_pago === 'EFECTIVO' ? ' Registrado en el arqueo de caja.' : ' Conciliá contra el banco de salida.'}
            </div>
          )}
          {retiro.estado === 'rechazado' && retiro.motivo_rechazo && (
            <Info label="Motivo del rechazo" value={retiro.motivo_rechazo} />
          )}

          {/* Verificación de la cuenta · con el flujo nuevo la titularidad se
              revisa ANTES del retiro (en Verificaciones bancarias). Acá solo
              confirmamos el estado; la imagen es opcional (sin error si falta). */}
          <div style={{ marginTop: 20 }}>
            <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 800, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2 }}>
              Verificación de la cuenta
            </p>
            {retiro.cuenta_estado === 'verificada' ? (
              <div style={{
                border: '1px solid rgba(15,138,87,0.25)', background: 'rgba(15,138,87,0.08)',
                borderRadius: 10, padding: '12px 14px', fontSize: 12.5, color: 'var(--ok-ink, #0f8a57)',
              }}>
                ✓ Cuenta verificada. La titularidad ya fue revisada.{' '}
                <a href="/neocore/verificaciones-bancarias" style={{ color: 'inherit', fontWeight: 700, textDecoration: 'underline' }}>
                  Ver verificaciones
                </a>
              </div>
            ) : (
              <div style={{
                border: '1px solid rgba(217,119,6,0.30)', background: 'rgba(217,119,6,0.08)',
                borderRadius: 10, padding: '12px 14px', fontSize: 12.5, color: 'var(--warning, #d97706)',
              }}>
                ⚠ La cuenta no está verificada{retiro.cuenta_estado ? ` (${retiro.cuenta_estado})` : ''}.
                Revisala en Verificaciones bancarias antes de acreditar.
              </div>
            )}
          </div>

          {esSolicitado && retiro.lote_id && (
            <div style={{
              marginTop: 20, border: '1px solid rgba(217,119,6,0.30)', background: 'rgba(217,119,6,0.08)',
              borderRadius: 10, padding: '12px 14px', fontSize: 12.5, color: 'var(--warning, #d97706)',
            }}>
              Este retiro está en un <strong>lote Express</strong> ({fmtQ(retiro.monto_lote ?? retiro.monto)} a transferir).
              Se acredita cuando Elder marca el lote como ejecutado (pestaña ⚡ Express). Para sacarlo, anulá el lote.
            </div>
          )}

          {esSolicitado && retiro.tipo === 'normal' && (
            <div style={{
              marginTop: 20, border: '1px solid var(--brand-300, var(--brand-200))', background: 'var(--brand-50)',
              borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--brand-700)' }}>
                ⚡ ¿El asociado lo quiere ya? Pasalo a Express (Q25 · recibe {fmtQ(retiro.monto - 25)} en menos de 30 min)
              </span>
              <input
                type="text" value={consent} onChange={(e) => setConsent(e.target.value)}
                placeholder='Cómo aceptó · ej. "WhatsApp 12:05: SÍ"'
                style={{ padding: '8px 10px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 12.5, fontFamily: 'inherit' }}
              />
              <button className="btn btn-ghost" onClick={pasarAExpress} disabled={submitting || !consent.trim()}
                style={{ borderColor: 'var(--brand)', color: 'var(--brand-700)', opacity: consent.trim() ? 1 : 0.5 }}>
                Pasar a Express
              </button>
            </div>
          )}

          {/* Acciones — solo para 'solicitado' (y fuera de un lote Express) */}
          {esSolicitado && !retiro.lote_id && (
            <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ fontSize: 11, color: 'var(--muted-foreground)', margin: 0, lineHeight: 1.5 }}>
                Validá que la cuenta sea del asociado antes de acreditar. Esto debita su ahorro.
              </p>

              {/* Forma de pago */}
              <div style={{ display: 'flex', gap: 8 }}>
                {(['TRANSFERENCIA', 'EFECTIVO'] as const).map((f) => {
                  const active = formaPago === f
                  return (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setFormaPago(f)}
                      style={{
                        all: 'unset', cursor: 'pointer', flex: 1, textAlign: 'center',
                        padding: '8px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                        border: '1px solid', borderColor: active ? 'var(--brand-500, var(--brand-700))' : 'var(--hairline)',
                        background: active ? 'var(--brand-500, var(--brand-700))' : '#fff',
                        color: active ? '#fff' : 'var(--ink-soft)',
                      }}
                    >
                      {f === 'TRANSFERENCIA' ? 'Transferencia' : 'Efectivo'}
                    </button>
                  )
                })}
              </div>

              {formaPago === 'TRANSFERENCIA' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    Sale de
                    <select
                      value={cuentaSalida}
                      onChange={(e) => setCuentaSalida(e.target.value)}
                      style={{ padding: '9px 11px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }}
                    >
                      {cuentasSalida.filter((c) => c.tipo === 'BANCO').map((c) => (
                        <option key={c.codigo} value={c.codigo}>{c.nombre}</option>
                      ))}
                    </select>
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    Referencia <span style={{ color: 'var(--danger, #a32d2d)' }}>*</span>
                    <input
                      type="text"
                      value={refBanco}
                      onChange={(e) => setRefBanco(e.target.value)}
                      placeholder="tu estado de cuenta"
                      style={{ padding: '9px 11px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 13, fontFamily: 'var(--font-mono, monospace)' }}
                    />
                  </label>
                </div>
              )}
              {formaPago === 'EFECTIVO' && (
                <p style={{ margin: 0, fontSize: 11, color: 'var(--muted-foreground)' }}>
                  Sale de <strong>Caja Principal</strong> · queda registrado como egreso en el arqueo de caja.
                </p>
              )}

              <input
                type="text"
                value={nota}
                onChange={(e) => setNota(e.target.value)}
                placeholder="Nota (opcional)"
                style={{
                  padding: '9px 11px', border: '1px solid var(--hairline)',
                  borderRadius: 8, fontSize: 13, fontFamily: 'inherit',
                }}
              />
              <button className="btn btn-primary" onClick={acreditar} disabled={submitting}>
                {submitting ? 'Acreditando…' : `Acreditar · transferido ${fmtQ(montoTransferir)}`}
              </button>

              <div style={{ marginTop: 8, paddingTop: 16, borderTop: '1px solid var(--hairline)' }}>
                <label style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, color: 'var(--muted-foreground)', textTransform: 'uppercase' }}>
                  O rechazar (motivo)
                </label>
                <input
                  type="text"
                  value={motivoRechazo}
                  onChange={(e) => setMotivoRechazo(e.target.value)}
                  placeholder="Ej. Titular no coincide con el asociado"
                  style={{
                    marginTop: 6,
                    padding: '10px 12px', border: '1px solid var(--hairline)',
                    borderRadius: 8, fontSize: 13, fontFamily: 'inherit', width: '100%',
                  }}
                />
                <button
                  onClick={rechazar}
                  disabled={submitting || !motivoRechazo.trim()}
                  style={{
                    marginTop: 8, width: '100%',
                    padding: '10px 14px', borderRadius: 8,
                    background: 'transparent',
                    border: '1px solid var(--danger, #a32d2d)',
                    color: 'var(--danger, #a32d2d)',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    opacity: submitting || !motivoRechazo.trim() ? 0.5 : 1,
                  }}
                >
                  Rechazar retiro
                </button>
              </div>
            </div>
          )}

          {/* Comprobante · anexo de consulta (al fondo) */}
          {imgUrl && (
            <div style={{ marginTop: 24 }}>
              <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 800, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2 }}>
                Comprobante · consulta
              </p>
              <a href={imgUrl} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                <img
                  src={imgUrl}
                  alt="Imagen de la cuenta"
                  style={{
                    width: '100%', borderRadius: 10, border: '1px solid var(--hairline)',
                    maxHeight: 340, objectFit: 'contain', background: 'var(--bg)',
                  }}
                />
              </a>
            </div>
          )}
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--hairline)' }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ width: '100%' }}>
            Cerrar
          </button>
        </div>
      </aside>
    </>
  )
}

function Info({ label, value, mono, highlight }: { label: string; value: string; mono?: boolean; highlight?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', gap: 16 }}>
      <span style={{ fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: 700 }}>
        {label}
      </span>
      <span style={{
        fontSize: 13, fontWeight: 600,
        color: highlight ? 'var(--brand-700)' : 'var(--ink)',
        fontFamily: mono ? 'IBM Plex Mono, monospace' : undefined,
        fontFeatureSettings: mono ? '"tnum"' : undefined,
        textAlign: 'right',
      }}>
        {value}
      </span>
    </div>
  )
}
